import { useState } from "react";
import { Download, Share2, Loader2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { logError } from "../lib/log";

const FORMATS = [
  { key: "post", label: "Instagram Post", ratio: "4:5" },
  { key: "story", label: "Story / Status", ratio: "9:16" },
  { key: "square", label: "Square", ratio: "1:1" },
];

export const ShareCard = ({ record }) => {
  const [fmt, setFmt] = useState("post");
  const [busy, setBusy] = useState(false);
  const url = `${API}/registries/${record.registry_id}/card.png?format=${fmt}`;

  const download = async () => {
    setBusy(true);
    try {
      const blob = await (await fetch(url)).blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `HYLUNER-${record.registry_id}-${fmt}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Star card downloaded");
    } catch (error) {
      logError("Card download failed", error);
      toast.error("Could not download the card");
    } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `HYLUNER-${record.registry_id}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "HYLUNER Registry",
          text: `${record.dedication_name} — ${record.registry_code}`,
        });
      } else {
        await download();
        toast.info("Card saved — attach it in Instagram or WhatsApp");
      }
    } catch (error) {
      logError("Share sheet cancelled or unavailable", error);
    } finally { setBusy(false); }
  };

  const copyPreviewLink = async () => {
    const link = `${API}/share/${record.registry_id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Preview link copied — paste it in any chat");
    } catch (error) {
      logError("Clipboard write blocked", error);
      toast.success(`Preview link: ${link}`, { duration: 12000 });
    }
  };

  return (
    <div className="hy-card p-8" data-testid="share-card-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="hy-label">Shareable Star Card</div>
          <p className="text-xs text-slate-500 mt-2">Ready-sized artwork for Instagram and WhatsApp.</p>
        </div>
        <div className="flex gap-2">
          {FORMATS.map((f) => (
            <button key={f.key} onClick={() => setFmt(f.key)} data-testid={`card-format-${f.key}`}
              className={`px-4 py-2.5 text-[0.6rem] uppercase tracking-widest border transition-colors ${fmt === f.key ? "border-amber-400 text-amber-200 bg-amber-500/10" : "border-amber-500/20 text-slate-400 hover:border-amber-500/45"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 flex justify-center bg-[#050811] border border-amber-500/15 p-6">
        <img key={fmt} src={url} alt="HYLUNER share card"
          className="max-h-[520px] w-auto hy-rise shadow-[0_20px_60px_rgba(0,0,0,.6)]"
          data-testid="share-card-preview" />
      </div>

      <div className="flex flex-wrap gap-4 mt-8">
        <button onClick={download} disabled={busy} className="hy-btn px-7 py-3.5 text-xs uppercase flex items-center gap-2 disabled:opacity-60" data-testid="card-download-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download image
        </button>
        <button onClick={share} disabled={busy} className="hy-btn-ghost px-7 py-3.5 text-xs uppercase flex items-center gap-2 disabled:opacity-60" data-testid="card-share-btn">
          <Share2 className="w-4 h-4" /> Share card
        </button>
        <button onClick={copyPreviewLink} className="hy-btn-ghost px-7 py-3.5 text-xs uppercase flex items-center gap-2" data-testid="card-copy-link-btn">
          <Link2 className="w-4 h-4" /> Copy preview link
        </button>
      </div>
      <p className="text-[0.7rem] text-slate-500 mt-5 leading-relaxed">
        The preview link unfurls with this artwork as the thumbnail in WhatsApp, iMessage, Slack,
        Discord, X and Facebook, and opens the registry page when tapped.
      </p>
    </div>
  );
};
